/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export type AISpeed = 'fast' | 'normal' | 'slow';

interface AccessibilitySettings {
  speechRate: number; // 0.5 - 2.0
  showCommandHints: boolean;
  showCaptions: boolean;
  aiSpeed: AISpeed;
}

interface VoiceCaption {
  id: string;
  text: string;
  timestamp: number;
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  setSpeechRate: (rate: number) => void;
  setShowCommandHints: (show: boolean) => void;
  setShowCaptions: (show: boolean) => void;
  setAiSpeed: (speed: AISpeed) => void;
  captions: VoiceCaption[];
  addCaption: (text: string) => void;
  clearCaptions: () => void;
  getAIDelay: (baseDelay: number) => number;
}

const AccessibilityContext = createContext<AccessibilityContextType | null>(null);

const STORAGE_KEY = 'game-accessibility-settings';

const getStoredSettings = (): AccessibilitySettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors
  }
  return { 
    speechRate: 1.0, 
    showCommandHints: true, 
    showCaptions: true,
    aiSpeed: 'normal'
  };
};

export const AccessibilityProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(getStoredSettings);
  const [captions, setCaptions] = useState<VoiceCaption[]>([]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Ignore storage errors (private mode, disabled storage, quota exceeded)
    }
  }, [settings]);

  // Auto-clear old captions after 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setCaptions(prev => prev.filter(c => now - c.timestamp < 5000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const setSpeechRate = useCallback((rate: number) => {
    setSettings(prev => ({ ...prev, speechRate: Math.max(0.5, Math.min(2, rate)) }));
  }, []);

  const setShowCommandHints = useCallback((show: boolean) => {
    setSettings(prev => ({ ...prev, showCommandHints: show }));
  }, []);

  const setShowCaptions = useCallback((show: boolean) => {
    setSettings(prev => ({ ...prev, showCaptions: show }));
  }, []);

  const setAiSpeed = useCallback((speed: AISpeed) => {
    setSettings(prev => ({ ...prev, aiSpeed: speed }));
  }, []);

  const addCaption = useCallback((text: string) => {
    const caption: VoiceCaption = {
      id: Math.random().toString(36).slice(2),
      text,
      timestamp: Date.now(),
    };
    setCaptions(prev => [...prev.slice(-4), caption]); // Keep last 5
  }, []);

  const clearCaptions = useCallback(() => {
    setCaptions([]);
  }, []);

  const getAIDelay = useCallback((baseDelay: number): number => {
    switch (settings.aiSpeed) {
      case 'fast': return baseDelay * 0.5;
      case 'slow': return baseDelay * 2;
      default: return baseDelay;
    }
  }, [settings.aiSpeed]);

  return (
    <AccessibilityContext.Provider value={{ 
      settings, 
      setSpeechRate, 
      setShowCommandHints, 
      setShowCaptions,
      setAiSpeed,
      captions, 
      addCaption, 
      clearCaptions,
      getAIDelay,
    }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};
